/**
 * @fileoverview Service for managing game questions and content.
 * 
 * This service handles fetching, caching, and managing questions for different game modes
 * with support for multiple languages and performance optimization.
 */

import { db } from '../firebase/config';
import { collection, query, where, getDocs, limit, orderBy, Timestamp } from 'firebase/firestore';
import { GameQuestion, GameType } from '../types';

class GameService {
  private readonly questionsCollection = 'gameQuestions';
  private readonly cache = new Map<string, GameQuestion[]>();
  private readonly cacheExpiry = 2 * 60 * 1000; // 2 minutes (reduced from 5)
  private readonly cacheTimestamps = new Map<string, number>();
  private readonly maxCacheCombos = 2; // Only keep 2 game/language combos in cache

  /**
   * Get questions for a specific game type with caching
   */
  async getQuestions(
    gameType: GameType,
    language: string = 'en',
    options: {
      difficulty?: 'easy' | 'medium' | 'hard' | 'mixed';
      category?: string;
      includeAdult?: boolean;
      limit?: number;
      ageRating?: 'G' | 'PG' | 'PG-13' | 'R';
    } = {}
  ): Promise<GameQuestion[]> {
    const cacheKey = `${gameType}-${language}-${JSON.stringify(options)}`;
    
    // Check cache first
    if (this.isCacheValid(cacheKey)) {
      // Move key to end to mark as recently used
      const value = this.cache.get(cacheKey);
      this.cache.delete(cacheKey);
      this.cache.set(cacheKey, value!);
      this.cacheTimestamps.set(cacheKey, Date.now());
      return value || [];
    }

    // Build query - only active questions
    let q = query(
      collection(db, this.questionsCollection),
      where('type', '==', gameType),
      where('isActive', '==', true), // Only active questions
      orderBy('createdAt', 'desc')
    );

    if (options.difficulty && options.difficulty !== 'mixed') {
      q = query(q, where('difficulty', '==', options.difficulty));
    }

    if (options.category) {
      q = query(q, where('category', '==', options.category));
    }

    // Age rating filter
    const maxAgeRating = options.ageRating || 'PG-13';
    const ageRatingOrder = ['G', 'PG', 'PG-13', 'R'];
    const maxIndex = ageRatingOrder.indexOf(maxAgeRating);
    
    if (maxIndex >= 0) {
      const allowedRatings = ageRatingOrder.slice(0, maxIndex + 1);
      q = query(q, where('ageRating', 'in', allowedRatings));
    }

    // Adult content filter
    if (options.includeAdult === false) {
      q = query(q, where('isAdult', '==', false));
    }

    if (options.limit) {
      q = query(q, limit(options.limit));
    }

    const snapshot = await getDocs(q);
    const questions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    })) as GameQuestion[];

    // Cache the results
    this.cache.set(cacheKey, questions);
    this.cacheTimestamps.set(cacheKey, Date.now());

    // LRU cache: if more than maxCacheCombos, evict oldest
    while (this.cache.size > this.maxCacheCombos) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey !== undefined) {
        this.cache.delete(oldestKey);
        this.cacheTimestamps.delete(oldestKey);
      } else {
        break;
      }
    }

    return questions;
  }

  /**
   * Get a random question for a game type
   */
  async getRandomQuestion(
    gameType: GameType,
    language: string = 'en',
    options: {
      difficulty?: 'easy' | 'medium' | 'hard' | 'mixed';
      category?: string;
      includeAdult?: boolean;
      excludeIds?: string[];
    } = {}
  ): Promise<GameQuestion | null> {
    const questions = await this.getQuestions(gameType, language, {
      ...options,
      limit: 50 // Get a batch to randomize from
    });

    if (questions.length === 0) return null;

    // Filter out excluded questions
    const availableQuestions = options.excludeIds 
      ? questions.filter(q => !options.excludeIds!.includes(q.id))
      : questions;

    if (availableQuestions.length === 0) return null;

    // Return random question
    return availableQuestions[Math.floor(Math.random() * availableQuestions.length)];
  }

  /**
   * Get Truth or Dare choice (Truth or Dare)
   */
  async getTruthDareChoice(
    language: string = 'en',
    options: {
      difficulty?: 'easy' | 'medium' | 'hard' | 'mixed';
      includeAdult?: boolean;
    } = {}
  ): Promise<{ type: 'truth' | 'dare'; question: GameQuestion } | null> {
    const type = Math.random() < 0.5 ? 'truth' : 'dare';
    const question = await this.getRandomQuestion('truth-dare', language, {
      ...options,
      category: type
    });

    return question ? { type, question } : null;
  }

  /**
   * Get trivia question with answer
   */
  async getTriviaQuestion(
    language: string = 'en',
    options: {
      difficulty?: 'easy' | 'medium' | 'hard' | 'mixed';
      category?: string;
      includeAdult?: boolean;
    } = {}
  ): Promise<GameQuestion | null> {
    return this.getRandomQuestion('trivia', language, options);
  }

  /**
   * Get "Would You Rather" question
   */
  async getWouldYouRatherQuestion(
    language: string = 'en',
    options: {
      difficulty?: 'easy' | 'medium' | 'hard' | 'mixed';
      includeAdult?: boolean;
    } = {}
  ): Promise<GameQuestion | null> {
    return this.getRandomQuestion('would-you-rather', language, options);
  }

  /**
   * Clear cache for a specific game type or all
   */
  clearCache(gameType?: GameType): void {
    if (gameType) {
      // Clear cache for specific game type
      for (const [key] of this.cache) {
        if (key.startsWith(gameType)) {
          this.cache.delete(key);
          this.cacheTimestamps.delete(key);
        }
      }
    } else {
      // Clear all cache
      this.cache.clear();
      this.cacheTimestamps.clear();
    }
  }

  /**
   * Check if cache is still valid
   */
  private isCacheValid(cacheKey: string): boolean {
    const timestamp = this.cacheTimestamps.get(cacheKey);
    if (!timestamp) return false;
    
    return Date.now() - timestamp < this.cacheExpiry;
  }

  /**
   * Preload questions for better performance
   */
  async preloadQuestions(gameType: GameType, language: string = 'en'): Promise<void> {
    // No-op: do not preload any questions
    return;
  }
}

export const gameService = new GameService(); 